import styled from 'styled-components';
import { components } from 'react-select';

import { TextField, SelectField, TagField, ParagraphField } from '~fields';
import { Gap, Button } from '~ui-kit';

import { fetchAdEmails } from './ducks';
import { searchCostCenters } from '../../services/api.helpers';

const formatUser = user => {
  const name = user.addisplayname || `${user.adgivenname} ${user.adsn}`;

  return {
    value: user.adname,
    label: `${name} <${user.ademail}>`,
    shortLabel: name,
    fullObject: user,
  };
};

const ShortSingleValue = props => (
  <components.SingleValue {...props}>
    {props.data.shortLabel}
  </components.SingleValue>
);

const PrependedValueContainer = ({ children, ...props }) => {
  const { owners } = props.selectProps;

  return (
    <components.ValueContainer {...props}>
      {owners.map(owner => (
        <MockMultiValue key={owner.value}>
          <MockMultiValueInner>{owner.label}</MockMultiValueInner>
        </MockMultiValue>
      ))}
      {children}
    </components.ValueContainer>
  );
};

const CustomPlaceholder = props => {
  const { owners } = props.selectProps;

  if (owners.length === 0) {
    return <components.Placeholder {...props} />;
  }

  return null;
};

const GroupEmailsForm = props => {
  const {
    formik: { values, setFieldValue },
  } = props;

  const getCostCenterOptions = async search => {
    if (!search || search.length < 2) {
      return Promise.resolve([]);
    }

    try {
      const data = await searchCostCenters(search);

      return Promise.resolve(
        data.content.map(costCenter => ({
          value: costCenter.id,
          label: `${costCenter.costCentre} ${costCenter.costCentreTitle}`,
        }))
      );
    } catch (e) {
      console.error('Error in fetching cost centers', e);
      return Promise.resolve([]);
    }
  };

  const oldUsers = [
    values.groupOwner1,
    values.groupOwner2,
    ...(values.groupUsers || []),
  ].filter(value => value);

  const count = oldUsers.length;

  const getUsers = async (search, oldUsers) => {
    // Start search after user has typed 3 chars
    if (!search || search.length < 3) {
      return Promise.resolve([]);
    }

    const isNewUser = user => !oldUsers?.some(p => p.value === user.adname);

    const users = await fetchAdEmails({ search });

    return Promise.resolve((users || []).filter(isNewUser).map(formatUser));
  };

  const clearDisabled = values.groupUsers?.length === 0;

  return (
    <>
      <ParagraphField name="infoParagraph" />
      <TagField name="groupType" />

      <Gap height="24px" />

      <SplitRow>
        <TextField
          name="groupName"
          fallbackPlaceholder="Kirjoita nimi"
          required
        />
        <TextField
          name="groupEmail"
          fallbackPlaceholder="Kirjoita osoite@osoite.fi"
          required
        />
      </SplitRow>

      <Gap height="24px" />

      <SelectField
        name="costCenter"
        async
        loadOptions={getCostCenterOptions}
        fallbackLabel="Maksajakustannuspaikka"
        required
        isClearable
      />

      <Gap height="24px" />

      <SplitRow>
        <SelectField
          name="groupOwner1"
          async
          loadOptions={search => getUsers(search, oldUsers)}
          components={{ SingleValue: ShortSingleValue }}
          fallbackLabel="Valitse käyttäjistä"
          required
          isClearable
        />
        <SelectField
          name="groupOwner2"
          async
          loadOptions={search => getUsers(search, oldUsers)}
          components={{ SingleValue: ShortSingleValue }}
          fallbackLabel="Valitse käyttäjistä"
          required
          isClearable
        />
      </SplitRow>

      <Gap height="24px" />

      <UserFieldContainer>
        <Counter>{count}</Counter>
        <CustomSelectField
          name="groupUsers"
          isMulti
          async
          styles={{
            valueContainer: base => ({
              ...base,
              display: oldUsers.length ? 'flex' : 'grid',
            }),
            indicatorsContainer: base => ({
              // Maybe bring this to ui-kit?
              ...base,
              alignItems: 'flex-end',
            }),
          }}
          loadOptions={search => getUsers(search, oldUsers)}
          components={{
            ValueContainer: PrependedValueContainer,
            Placeholder: CustomPlaceholder,
          }}
          fallbackPlaceholder="Kirjoita tai valitse nimi"
          isClearable={false}
          owners={[values.groupOwner1, values.groupOwner2].filter(
            value => value
          )}
          required
        />
        <Gap height="12px" />
        <ClearButton
          bgColor={!clearDisabled && 'white'}
          color={!clearDisabled && 'var(--textColor)'}
          border={!clearDisabled && '1px solid var(--lightBlue)'}
          disabled={clearDisabled}
          onClick={() => setFieldValue('groupUsers', [])}
        >
          Tyhjennä kaikki valinnat
        </ClearButton>
      </UserFieldContainer>
    </>
  );
};

const SplitRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 30px;

  & > * {
    flex: 1 1 0;
  }
`;

const MockMultiValue = styled.div`
  display: flex;
  min-width: 0;
  background-color: hsl(0, 0%, 90%);
  border-radius: 2px;
  margin: 2px;
  box-sizing: border-box;
  z-index: 0;
`;

const MockMultiValueInner = styled.div`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  border-radius: 2px;
  color: hsl(0, 0%, 20%);
  font-size: 85%;
  padding: 3px 6px;
  box-sizing: border-box;
`;

const CustomSelectField = styled(SelectField)`
  max-width: var(--field-width-wide);
`;

const UserFieldContainer = styled.div`
  position: relative;
`;

const Counter = styled.div`
  position: absolute;
  right: 0;
  color: var(--primaryColor);
  opacity: 0.5;
  user-select: none;
`;

const ClearButton = styled(Button)``;

export default GroupEmailsForm;
